import classNames from 'classnames';
import BButton from 'components/common/BButton';
import BSpinnerInline from 'components/common/BSpinnerInline';
import { SupportedDemandType } from 'enums/SupportedDemandType';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useMemo } from 'react';
import { CalculatePricing } from 'types/demand.type';

interface PriceWidgetCTAButtonProps {
  onClick?: () => void;
  supportedDemandTypes: SupportedDemandType[];
  pricing?: CalculatePricing.MappedResponseWithDuration;
  className?: string;
  fluid?: boolean;
  isCalculateAmountRequestSent: boolean;
  isCalcutePricingRequestOngoing?: boolean;
  isDemandRequestOngoing?: boolean;
}

const PriceWidgetCTAButton: FunctionComponent<PriceWidgetCTAButtonProps> = ({
  onClick,
  supportedDemandTypes,
  pricing,
  className,
  fluid = false,
  isCalculateAmountRequestSent,
  isCalcutePricingRequestOngoing,
  isDemandRequestOngoing = false,
}) => {
  const { t } = useTranslation('vehicle');
  const hasPrice = pricing?.totalAmount;

  const checkonlyImmediatelyReservable = () => {
    const isImmediateReservation = supportedDemandTypes.includes(
      SupportedDemandType.IMMEDIATE_RESERVATION
    );
    return (
      isImmediateReservation && (hasPrice || !isCalculateAmountRequestSent)
    );
  };

  const checkIsReservationRequestable = () => {
    const isReservationRequest = supportedDemandTypes.includes(
      SupportedDemandType.RESERVATION
    );
    return isReservationRequest && (hasPrice || !isCalculateAmountRequestSent);
  };

  const checkIsRequestPrice = () => {
    const isImmediateReservation = supportedDemandTypes.includes(
      SupportedDemandType.IMMEDIATE_RESERVATION
    );
    const isReservationRequest = supportedDemandTypes.includes(
      SupportedDemandType.RESERVATION
    );
    const isPrice = supportedDemandTypes.includes(SupportedDemandType.PRICE);
    const isImmediateReservationWithoutPrice =
      isImmediateReservation && !hasPrice;
    const isReservationRequestWithoutPrice = isReservationRequest && !hasPrice;
    return (
      isPrice ||
      isImmediateReservationWithoutPrice ||
      isReservationRequestWithoutPrice
    );
  };

  const buttonLabel = useMemo(() => {
    if (checkonlyImmediatelyReservable()) {
      return 'priceWidget.actions.immediatelyReserve';
    }
    if (checkIsReservationRequestable()) {
      return 'priceWidget.actions.reservationRequest';
    }
    if (checkIsRequestPrice()) {
      return 'priceWidget.actions.requestPrice';
    }
    const isAvailability = supportedDemandTypes.includes(
      SupportedDemandType.AVAILABILITY
    );
    if (isAvailability && (hasPrice || !isCalculateAmountRequestSent)) {
      return 'priceWidget.actions.checkAvailibility';
    } else {
      return 'priceWidget.actions.requestPrice';
    }
  }, [pricing, supportedDemandTypes]);

  return (
    <BButton
      className={classNames('!py-3', className)}
      fluid={fluid}
      type="submit"
      onClick={onClick}
      disabled={isCalcutePricingRequestOngoing || isDemandRequestOngoing}
    >
      {isCalcutePricingRequestOngoing || isDemandRequestOngoing ? (
        <BSpinnerInline size={24} className="mx-auto" />
      ) : (
        t(buttonLabel)
      )}
    </BButton>
  );
};

export default PriceWidgetCTAButton;
